<template>
	<section class="mgm" :class="{ 'mgm--no-image': !showImage }">
		<div class="mgm__container">
			<div class="mgm__content">
				<div class="mgm__text">
					<p class="mgm__eyebrow">{{ $t('mgm.eyebrow') }}</p>
					<h2>{{ $t('mgm.title') }}</h2>
					<p class="mgm__description">{{ $t('mgm.description') }}</p>
				</div>

				<div class="mgm__form">
					<div class="mgm__field">
						<label for="mgm-invite-email">{{ $t('mgm.emailLabel') }}</label>
						<div class="mgm__field-row">
							<FormInput
								data-qa="mgm-invite-email-input"
								v-model="inviteEmail"
								:label="$t('mgm.emailLabel')"
								input-id="mgm-invite-email"
								input-type="email"
								:placeholder="$t('mgm.emailPlaceholder')"
								:show-label="false"
							/>
							<AppButton
								data-qa="mgm-invite-send-button"
								variant="primary"
								:small="true"
								icon="sendArrow"
								:disabled="inviteSending"
								@click.prevent="handleSendInvite"
							>
								{{ $t('mgm.sendButton') }}
							</AppButton>
						</div>
						<span
							v-if="inviteMessage"
							class="mgm__feedback"
							:class="{ 'mgm__feedback--error': inviteHasError }"
							v-bind="{ 'data-qa': inviteHasError ? 'mgm-invite-error' : 'mgm-invite-success' }"
						>
							{{ inviteMessage }}
						</span>
					</div>

					<div class="mgm__field">
						<label for="mgm-ref-link">{{ $t('mgm.linkLabel') }}</label>
						<div class="mgm__field-row">
							<FormInput
								data-qa="mgm-referral-link-input"
								:model-value="referralLink"
								:label="$t('mgm.linkLabel')"
								input-id="mgm-ref-link"
								input-type="text"
								:placeholder="$t('mgm.linkAriaLabel')"
								:is-disabled="true"
								:show-label="false"
							/>
							<AppButton
								data-qa="mgm-referral-copy-button"
								variant="primary"
								:small="true"
								icon="copy"
								@click.prevent="handleCopyLink"
							>
								{{ $t('mgm.copyButton') }}
							</AppButton>
						</div>
						<span
							v-if="copyMessage"
							class="mgm__feedback"
							:class="{ 'mgm__feedback--error': copyHasError }"
							v-bind="{ 'data-qa': copyHasError ? 'mgm-copy-error' : 'mgm-copy-success' }"
						>
							{{ copyMessage }}
						</span>
					</div>
				</div>
			</div>

			<div v-if="showImage" class="mgm__media">
				<img data-qa="mgm-hero-image" :src="heroImageResolved" :alt="$t('mgm.imageAlt')" loading="lazy" />
			</div>
		</div>

		<div
			v-if="isInviteSuccessPopupOpen"
			class="mgm__popup-overlay"
			data-qa="mgm-invite-success-modal"
			role="dialog"
			aria-modal="true"
			:aria-labelledby="popupDialogTitleId"
			:aria-describedby="popupDialogDescriptionId"
			@click.self="closeInviteSuccessPopup"
		>
			<div class="mgm__popup">
				<button
					type="button"
					class="mgm__popup-close"
					data-qa="mgm-invite-success-close-button"
					:aria-label="$t('mgm.popupCloseAriaLabel')"
					@click.prevent="closeInviteSuccessPopup"
				>
					<img :src="popupCloseIconResolved" alt="" loading="lazy" />
				</button>

				<div class="mgm__popup-icon-wrap" aria-hidden="true">
					<img :src="popupIconResolved" alt="" loading="lazy" />
				</div>

				<div class="mgm__popup-content">
					<h3 :id="popupDialogTitleId" class="mgm__popup-title">
						<span>{{ $t('mgm.popupTitleLine1') }}</span>
						<span>{{ $t('mgm.popupTitleLine2') }}</span>
					</h3>
					<p :id="popupDialogDescriptionId" class="mgm__popup-description">
						<span>{{ $t('mgm.popupDescriptionLine1') }}</span>
						<span>{{ $t('mgm.popupDescriptionLine2') }}</span>
					</p>
				</div>
			</div>
		</div>
	</section>
</template>

<script>
import AppButton from '@/components/ui/AppButton.vue'
import FormInput from '@/components/form/FormInput.vue'
import mgmHeroDefault from '@/assets/img/mock/mgm-hero.png'
import mgmSuccessInviteIcon from '@/assets/img/simbolo-pop.png'
import mgmPopupCloseIcon from '@/assets/img/mock/mgm-popup-close.svg'

export default {
	name: 'MGMComponent',
	components: {
		AppButton,
		FormInput,
	},
	emits: ['send-invite', 'copy-success', 'copy-error'],
	props: {
		showImage: {
			type: Boolean,
			default: true,
		},
		referralLink: {
			type: String,
			required: true,
		},
		heroImage: {
			type: String,
			default: '',
		},
		sendInviteRequest: {
			type: Function,
			default: null,
		},
	},
	data() {
		return {
			inviteEmail: '',
			inviteMessage: '',
			inviteHasError: false,
			inviteSending: false,
			copyMessage: '',
			copyHasError: false,
			isInviteSuccessPopupOpen: false,
		}
	},
	computed: {
		heroImageResolved() {
			return this.heroImage || mgmHeroDefault
		},
		popupIconResolved() {
			return mgmSuccessInviteIcon
		},
		popupCloseIconResolved() {
			return mgmPopupCloseIcon
		},
		popupDialogTitleId() {
			return `mgm-popup-title-${this._uid}`
		},
		popupDialogDescriptionId() {
			return `mgm-popup-description-${this._uid}`
		},
	},
	mounted() {
		window.addEventListener('keydown', this.handleGlobalKeydown)
	},
	beforeDestroy() {
		window.removeEventListener('keydown', this.handleGlobalKeydown)
		document.body.style.overflow = ''
	},
	watch: {
		isInviteSuccessPopupOpen(isOpen) {
			document.body.style.overflow = isOpen ? 'hidden' : ''
		},
	},
	methods: {
		isValidEmail(email) {
			return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
		},
		handleGlobalKeydown(event) {
			if (event.key === 'Escape' && this.isInviteSuccessPopupOpen) {
				this.closeInviteSuccessPopup()
			}
		},
		openInviteSuccessPopup() {
			this.isInviteSuccessPopupOpen = true
		},
		closeInviteSuccessPopup() {
			this.isInviteSuccessPopupOpen = false
		},
		async handleSendInvite() {
			const inviteEmailNormalized = (this.inviteEmail || '').trim()

			if (!this.isValidEmail(inviteEmailNormalized)) {
				this.inviteHasError = true
				this.inviteMessage = this.$t('mgm.invalidEmail')
				return
			}

			this.inviteMessage = ''

			if (typeof this.sendInviteRequest === 'function') {
				this.inviteSending = true
				try {
					await this.sendInviteRequest(inviteEmailNormalized)
					this.inviteHasError = false
					this.inviteMessage = this.$t('mgm.sendSuccess')
					this.$emit('send-invite', { email: inviteEmailNormalized })
					this.openInviteSuccessPopup()
					this.inviteEmail = ''
				} catch (e) {
					this.inviteHasError = true
					const code = e?.response?.data?.message
					if (code === 'user-is-member') {
						this.inviteMessage = this.$t('mgm.sendErrorAlreadyMember')
					} else if (code === 'mgm-invite-already-sent') {
						this.inviteMessage = this.$t('mgm.sendErrorAlreadyInvited')
					} else {
						this.inviteMessage = this.$t('mgm.sendError')
					}
				} finally {
					this.inviteSending = false
				}
				return
			}

			this.inviteHasError = false
			this.inviteMessage = this.$t('mgm.sendSuccess')
			this.$emit('send-invite', { email: inviteEmailNormalized })
			this.openInviteSuccessPopup()
			this.inviteEmail = ''
		},
		async handleCopyLink() {
			try {
				if (!this.referralLink) {
					throw new Error('empty-referral-link')
				}

				if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
					await navigator.clipboard.writeText(this.referralLink)
				} else {
					this.fallbackCopy(this.referralLink)
				}

				this.copyHasError = false
				this.copyMessage = this.$t('mgm.copySuccess')
				this.$emit('copy-success', { referralLink: this.referralLink })
			} catch (error) {
				this.copyHasError = true
				this.copyMessage = this.$t('mgm.copyError')
				this.$emit('copy-error', { referralLink: this.referralLink, error })
			}
		},
		fallbackCopy(text) {
			const textarea = document.createElement('textarea')
			textarea.value = text
			textarea.setAttribute('readonly', '')
			textarea.style.position = 'absolute'
			textarea.style.left = '-9999px'
			document.body.appendChild(textarea)
			textarea.select()
			document.execCommand('copy')
			document.body.removeChild(textarea)
		},
	},
}
</script>

<style lang="sass" scoped>
@use '../../assets/sass/utilities/_colors' as *
@use '../../assets/sass/utilities/_responsive' as *
@use '../../assets/sass/utilities/_mixin' as *

.mgm
	background: var(--lg-light-gray-100)
	border-radius: 2rem
	margin-inline: var(--padding-mobile)
	padding: 0

	+respond-to($breakpoint-xl-min)
		margin-inline: 0

	&__container
		display: flex
		flex-direction: column
		gap: 1rem

		+respond-to($breakpoint-xl-min)
			display: grid
			grid-template-columns: minmax(560px, 624px) minmax(420px, 1fr)
			align-items: stretch
			gap: 1.5rem

	&__content
		background: var(--white)
		border-radius: 2rem
		padding: 1.5rem
		display: flex
		flex-direction: column
		justify-content: center
		gap: 1.5rem

		+respond-to($breakpoint-xl-min)
			padding: clamp(1.5rem, 3vw, 3rem)
			gap: 0

	&__text
		display: flex
		flex-direction: column
		gap: 0.5rem

		+respond-to($breakpoint-xl-min)
			gap: 1rem

		h2
			font-family: var(--lgei-headline)
			font-size: 1.75rem
			line-height: 1.05
			color: var(--club--title-default)
			text-wrap: balance

			+respond-to($breakpoint-xl-min)
				font-size: clamp(2rem, 2.8vw, 3rem)

	&__eyebrow
		color: var(--lg-red-active)
		font-family: var(--lgei-headline)
		font-size: 1rem
		font-weight: 600
		line-height: 1.2

		+respond-to($breakpoint-xl-min)
			font-size: 1.5rem

	&__description
		color: var(--club--text-default)
		font-size: 1rem
		font-family: var(--lgei-text)
		font-weight: 600
		line-height: 1.2

		+respond-to($breakpoint-xl-min)
			font-size: clamp(1.05rem, 1.4vw, 1.3125rem)
			line-height: 1.4

	&__form
		display: flex
		flex-direction: column
		gap: 1.5rem

		+respond-to($breakpoint-xl-min)
			margin-top: 1.75rem
			gap: 1rem

	&__field
		display: flex
		flex-direction: column
		gap: 0.5rem

		label
			font-family: var(--lgei-text)
			font-size: 0.875rem
			line-height: 1.2
			color: var(--lg-dark-gray-200)

	&__field-row
		display: flex
		flex-direction: column
		gap: 0.5rem

		+respond-to($breakpoint-lg-min)
			display: grid
			grid-template-columns: 1fr auto
			align-items: center

		:deep(.box--form)
			width: 100%
			margin-bottom: 0
			flex: 1 1 auto

		:deep(.box--form .input-wrapper)
			width: 100%

		:deep(.box--form .input-wrapper input)
			width: 100%
			height: 2.5rem
			border-radius: 0.5rem
			border: 1px solid var(--lg-light-gray-300)
			background: var(--lg-light-gray-100)
			padding-inline: 0.75rem
			font-family: var(--lgei-text)
			font-size: 0.875rem
			color: var(--color-grey-1400)
			transition: var(--transition-cubic)

			&:focus-visible
				outline: none
				border-color: var(--lg-red-active)
				box-shadow: 0 0 0 2px rgba(var(--lg-red-active-rgb), .22)

			&[readonly], &[disabled]
				overflow: hidden
				text-overflow: ellipsis

		:deep(.box--form .alertErro)
			display: none

		:deep(.AppButton)
			width: 100%
			min-width: 5.875rem

			+respond-to($breakpoint-lg-min)
				width: fit-content

	&__feedback
		font-family: var(--lgei-text)
		font-size: 0.8125rem
		color: var(--lg-green-validation)

		&--error
			color: var(--lg-red-active)

	&__media
		display: flex
		align-items: center
		justify-content: center
		overflow: hidden
		border-radius: 999px
		aspect-ratio: 350 / 212
		background: rgba(var(--lg-red-active-rgb), .05)

		img
			width: 100%
			height: 100%
			object-fit: cover

		+respond-to($breakpoint-xl-min)
			border-radius: 14rem
			aspect-ratio: auto
			min-height: 479px

	&--no-image
		background: linear-gradient(159deg, var(--lg-mid-gray-300), var(--lg-dark-gray-300))
		border-radius: 1rem
		padding: 1.5rem
		margin-inline: 0
		box-shadow: inset 0 0.5px 2px rgba(var(--white-rgb), .6), inset 0 -1px 2px var(--black)

		+respond-to($breakpoint-xl-min)
			padding: 2.5rem

		.mgm__container
			display: block
			grid-template-columns: none
			gap: 1.5rem

		.mgm__content
			width: 100%
			background: transparent
			border-radius: 0
			padding: 0
			gap: 2rem

			+respond-to($breakpoint-xl-min)
				display: flex
				gap: 2.5rem

		.mgm__eyebrow
			display: none

		.mgm__text
			max-width: 100%
			gap: 1.125rem

			h2
				color: var(--white)
				font-size: 2rem
				line-height: 1.2

		.mgm__description
			max-width: 100%
			color: var(--lg-light-gray-300)
			font-size: 1.25rem
			font-weight: 400
			line-height: 1.3

		.mgm__form
			width: 100%
			max-width: none
			min-width: 0
			margin-top: 0
			gap: 2rem

		.mgm__field label
			color: var(--white)
			font-family: var(--lgei-headline)

		.mgm__field-row
			align-items: end

			:deep(.box--form .input-wrapper input)
				background: var(--white)

		+respond-to($sm-device-max)
			.mgm__text h2
				font-size: 1.75rem

			.mgm__description
				font-size: 1rem

	&__popup-overlay
		position: fixed
		inset: 0
		background: rgba(var(--black-rgb), .25)
		backdrop-filter: blur(8px)
		display: flex
		align-items: center
		justify-content: center
		z-index: 1000
		padding: 1rem

	&__popup
		position: relative
		width: min(100%, 33rem)
		background: var(--white)
		border-radius: 1.5rem
		padding: 2.25rem 1.5rem 1.75rem
		display: flex
		flex-direction: column
		align-items: center
		gap: 1rem
		box-shadow: 0 20px 20px rgba(var(--black-rgb), .06)
		text-align: center

		+respond-to($breakpoint-lg-min)
			padding: 3rem
			gap: 1rem

	&__popup-close
		position: absolute
		top: 1rem
		right: 1rem
		width: 1.25rem
		height: 1.25rem
		padding: 0
		border: 0
		background: transparent
		display: inline-flex
		align-items: center
		justify-content: center
		cursor: pointer

		img
			width: 100%
			height: 100%
			object-fit: contain

		&:focus-visible
			outline: none
			box-shadow: 0 0 0 2px rgba(var(--lg-red-active-rgb), .22)
			border-radius: 2px

	&__popup-icon-wrap
		width: 4.625rem
		height: 4.625rem
		border-radius: 9999px
		background: #f0ece4
		display: flex
		align-items: center
		justify-content: center
		overflow: hidden
		position: relative

		img
			width: 100%
			height: 100%
			object-fit: contain

	&__popup-content
		display: flex
		flex-direction: column
		gap: 0.5rem

	&__popup-title
		font-family: var(--lgei-headline)
		font-weight: 600
		font-size: 1.75rem
		line-height: 1.2
		color: var(--lg-red-active)
		margin: 0

		span
			display: block

		+respond-to($breakpoint-lg-min)
			font-size: 2rem

	&__popup-description
		font-family: var(--lgei-text)
		font-size: 1rem
		line-height: 1.3
		color: var(--club--text-default)
		margin: 0

		span
			display: block

		+respond-to($breakpoint-lg-min)
			font-size: 1.25rem
</style>
